<template>
  <v-card class="mb-4" style="height: 460px;">
    <v-card-title class="d-flex justify-space-between align-center">
      {{ $t('documentUploads') }}
      <v-btn-toggle
        v-model="selectedMetric"
        mandatory
        class="ml-4"
      >
        <v-btn value="count">{{ $t('count') }}</v-btn>
        <v-btn value="size">{{ $t('size') }}</v-btn>
      </v-btn-toggle>
    </v-card-title>
    <v-card-text>
      <v-chart class="chart" ref="chartRef" :option="chartOption" />
    </v-card-text>         
  </v-card>
</template>

<script setup>
import { ref, watch, onMounted, defineProps, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import { TooltipComponent, TitleComponent, GridComponent, LegendComponent } from "echarts/components";
import VChart from "vue-echarts";

use([CanvasRenderer, LineChart, TooltipComponent, TitleComponent, GridComponent, LegendComponent]);

const props = defineProps({
  documents: {
    type: Array,
    required: true
  }
});

const store = useStore();
const { t } = useI18n();
const chartColor = computed(() => store.getters.chartFontColor);

const chartRef = ref(null);
const chartOption = ref({});
const selectedMetric = ref('count');

const processedData = computed(() => {
  const dateCountMap = new Map();
  const dateSizeMap = new Map();

  props.documents.forEach(doc => {
    const date = new Date(doc.createdDate).toISOString().split('T')[0];
    dateCountMap.set(date, (dateCountMap.get(date) || 0) + 1);
    dateSizeMap.set(date, (dateSizeMap.get(date) || 0) + (doc.fileSize || 0));
  });

  const sortedDates = Array.from(dateCountMap.keys()).sort();
  const cumulativeCounts = [];
  const cumulativeSizes = [];
  let cumulativeCount = 0;
  let cumulativeSize = 0;

  sortedDates.forEach(date => {
    cumulativeCount += dateCountMap.get(date);
    cumulativeSize += dateSizeMap.get(date);
    cumulativeCounts.push(cumulativeCount);
    cumulativeSizes.push(cumulativeSize);
  });

  return {
    dates: sortedDates,
    counts: cumulativeCounts,
    sizes: cumulativeSizes
  };
});

const formatSize = (bytes) => {
  const GB = bytes / (1024 * 1024 * 1024);
  return GB.toFixed(2);
};

const updateChartOption = () => {
  const { dates, counts, sizes } = processedData.value;
  const isCount = selectedMetric.value === 'count';

  chartOption.value = {
    tooltip: {
      trigger: 'axis',
      confine: true,
      enterable: true,
      extraCssText: 'max-width: 320px; overflow-x: auto;',
      formatter: function(params) {
        const date = params[0].name;
        const value = params[0].value;
        const metricName = isCount ? t('totalCount') : 'Total Size';
        const formattedValue = isCount ? value : `${formatSize(value)} GB`;
        return `Date: ${date}<br>${metricName}: ${formattedValue}`;
      }
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: dates,
      axisLabel: {
        color: chartColor.value,
        rotate: 45
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: chartColor.value,
        formatter: isCount ? '{value}' : function(value) {
          return formatSize(value) + ' GB';
        }
      }
    },
    series: [{
      data: isCount ? counts : sizes,
      type: 'line',
      name: isCount ? 'Cumulative Count' : 'Cumulative Size',
      smooth: true,
      itemStyle: {
        color: '#5470c6'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [{
            offset: 0,
            color: 'rgba(84,112,198,0.5)'
          }, {
            offset: 1,
            color: 'rgba(84,112,198,0.1)'
          }]
        }
      }
    }],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      containLabel: true
    }
  };
};

watch(() => props.documents, () => {
  updateChartOption();
}, { deep: true });

watch(selectedMetric, () => {
  updateChartOption();
});

watch(() => chartColor.value, () => {
  updateChartOption();
});

onMounted(() => {
  updateChartOption();
  window.addEventListener('resize', () => {
    if (chartRef.value) {
      chartRef.value.resize();
    }
  });
});
</script>

<style scoped>
.chart {
  height: 400px;
}
</style>